import React, { FC, useState, useCallback, useMemo, useEffect } from "react";

import {
  Box,
} from "@pixel/core";
import {
  RenderCustomFilterCallback,
  ListAppliedSearchFilterProps,
  ListAppliedSearchFilterValueProps,
  ListSearchFilterCustomProps
} from "@pixel/core/ListBase";
import AutoSelectAsyncList from '@pixel/core/AutoSelectAsyncList'
import useEmployeeAutocomplete from '../hooks/useEmployeeAutocomplete'
import { useLocalizedText } from '../contexts/Localization/localizationHooks';
import { LocalizedText } from '../contexts/Localization/LocalizationTypes';
import ToolTip from "@pixel/core/Tooltip";
import { Chip } from "@mui/material";
import { ChangeAppliedFilterCustomCallback } from "@pixel/core/ListSearchFilter";

interface IUsersData {
  label: string
  value: string
  email?: string
}

interface SearchFilterAsyncUserAutoCompleteProps extends RenderCustomFilterCallback {
  value: Array<IUsersData>
  onChange: (event: any, newValue: Array<IUsersData>) => void
  optional?: boolean
  searchFilter: ListSearchFilterCustomProps
  appliedSearchFilter: ListAppliedSearchFilterProps
  onChangeAppliedFilter: ChangeAppliedFilterCustomCallback
}

export const transformUserData = (resultSet: any): any => {
  let newUserList: any = [];
  resultSet.forEach((e: any) => {
      let user = {
          value: e.EId,
          label: e.fullName,
          email: e.email
      }
      newUserList.push(user);
  });
  return newUserList;
}

const SearchFilterAsyncUserAutoComplete: FC<SearchFilterAsyncUserAutoCompleteProps> = ({
  value,
  onChange,
  optional = false,
  searchFilter,
  appliedSearchFilter,
  onChangeAppliedFilter
}) => {
  const VALCONTEXT_LIST: any = null

  const [stateAppliedFitler, setStateAppliedFitler] = useState(appliedSearchFilter);
  const [appliedFilterUpdated, setAppliedFilterUpdated] = useState(false);

  useEffect(() => {
    if (stateAppliedFitler !== appliedSearchFilter) {
      setStateAppliedFitler(appliedSearchFilter);
      setAppliedFilterUpdated(true)
    }
  }, [appliedSearchFilter]);

  const appliedFitlerListValues = useMemo(() => {
    return stateAppliedFitler && stateAppliedFitler.filterValues.filter((fVal) => fVal.context === VALCONTEXT_LIST) || [];
  }, [stateAppliedFitler]);
  const [tmpAppliedFitlerListValuesState, setTmpAppliedFitlerListValuesState] = useState(appliedFitlerListValues);
  useEffect(() => {
    if (tmpAppliedFitlerListValuesState !== appliedFitlerListValues) {
      setTmpAppliedFitlerListValuesState(appliedFitlerListValues);
    }
  }, [appliedFitlerListValues]);

  const appliedFitlerListValuesOptions = useMemo(() => {
    if (tmpAppliedFitlerListValuesState) {
      return tmpAppliedFitlerListValuesState.map((fVal) => ({
        value: fVal.value,
        label: fVal.label,
      }));
    } else {
      return [];
    }
  }, [tmpAppliedFitlerListValuesState]);

  const [options, setOptions] = useState(appliedFitlerListValuesOptions);

  useEffect(() => {
    if (appliedFitlerListValuesOptions !== options) {
      setOptions(appliedFitlerListValuesOptions);
    }
  }, [appliedFitlerListValuesOptions]);

  useEffect(() => {
    if (!appliedFilterUpdated) {
      handleChangeAppliedFitlerAll()
    } else {
      setAppliedFilterUpdated(false)
    }
  }, [options]);

  const handleChangeSelect = (e: any, menuItemSelect: any) => {
    setTmpAppliedFitlerListValuesState(menuItemSelect);
    setTextInput('');
  }

  const handleChangeAppliedFitlerAll = () => {
    const hasValuesNeededForAppliedFilter = (options.length >= 1);
    if (hasValuesNeededForAppliedFilter) {

      const changedAppliedFilter: ListAppliedSearchFilterProps = {
        searchFilterId: searchFilter.id,
        displayLabel: `${searchFilter.label}: ${options[0].label}`,
        filterValues: []
      };
      options.forEach((option) => {
        const filterOption: ListAppliedSearchFilterValueProps = {
          value: option.value,
          label: option.label,
          context: VALCONTEXT_LIST
        }
        changedAppliedFilter.filterValues.push(filterOption);
      })
      onChangeAppliedFilter(searchFilter, changedAppliedFilter);
    } else {
      onChangeAppliedFilter(searchFilter, null);
    }
  }

  const renderVal = useCallback((selected) => {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map(({ value, label }) => (
          <Chip key={value} label={label} />
        ))}
      </Box>
    )
  }, [])

  const ODS_CREATED_BY = useLocalizedText(LocalizedText.ODS_CREATED_BY)

  const [textInput, setTextInput] = useState('');
  const [page, setPage] = useState(0);
  const [allEmployees, setAllEmployees] = useState<Array<IUsersData>>([]);

  const {
    employees,
    employeesCount,
    loading,
    error,
  } = useEmployeeAutocomplete({
    page,
    keywords: textInput,
    onNewResults: (response) => {
      setAllEmployees(transformUserData(response.employees))
    },
    onNextPageResults: (response) => {
      let employees = transformUserData(response.employees);
      setAllEmployees(currentOptions => [...currentOptions, ...employees]);
    }
  })

  const renderCustomOptionChip = (option: IUsersData, index: number, getTagProps: Function) => {
    const label = option?.label;
    if (label?.length > 10) {
      const truncatedLabel = label?.length > 10 ? `${label?.substring(0, 10)}...` : label;
      return (
        <ToolTip title={label} key={option.value}>
          <Chip label={truncatedLabel} {...getTagProps({ index })} />
        </ToolTip>
      );
    } else {
      return <Chip label={label} key={option.value}  {...getTagProps({ index })} />
    }
  }

  return (
    <AutoSelectAsyncList
      fieldLabel={ODS_CREATED_BY}
      paginated
      multiple
      id={searchFilter.id}
      placeholder={searchFilter.placeholder}
      debounceInput={100}
      loading={loading}
      loadingNextPage={loading}
      noResults={allEmployees.length === 0}
      options={allEmployees}
      value={options}
      inputValue={textInput}
      onDebouncedKeyStroke={(newInput: string) => {
        setPage(0)
        setTextInput(newInput)
      }}
      onFetchNextPage={() => setPage(current => current + 1)}
      currentPage={page}
      disableCloseOnSelect={true}
      renderOption={(props, option: IUsersData) => (
        <div style={{ display: 'flex', alignItems: 'center' }} {...props}>
          <div style={{ padding: '5px 0' }}>
            <span style={{ color: '#1872cc', display: 'block', lineHeight: '18px' }}>{option.label}</span>
            <span style={{ lineHeight: '18px', display: 'block' }}>{option.email}</span>
          </div>
        </div>
      )}
      onChange={handleChangeSelect}
      getOptionLabel={(option: IUsersData) => option.label}
      getValueLabel={(option: IUsersData) => option.label}
      isOptionEqualToValue={(option: IUsersData, value: IUsersData) => option.value === value.value}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option: IUsersData, index: number) =>
          renderCustomOptionChip(option, index, getTagProps)
        )
      }
    />
  )
}

export default SearchFilterAsyncUserAutoComplete;